// The same runtime contract is used by admin and portal. No localStorage:
// appearance must never leak between applications or survive a removed override.
const colorNames = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'white'];
const hex = value => typeof value === 'string' && /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(value) ? value : null;
const rgb = color => {
    const full = color.length === 4 ? color.slice(1).split('').map(part => part + part).join('') : color.slice(1);
    return [0, 2, 4].map(index => parseInt(full.slice(index, index + 2), 16));
};
const luminance = color => rgb(color).map(channel => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}).reduce((sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index], 0);
export const colorContrastRatio = (first, second) => {
    const values = [luminance(first), luminance(second)].sort((a, b) => b - a);
    return (values[0] + 0.05) / (values[1] + 0.05);
};
const contrast = (color, preferred) => {
    if (hex(preferred) && colorContrastRatio(color, preferred) >= 4.5) return preferred;
    return colorContrastRatio(color, '#17212b') >= colorContrastRatio(color, '#fff') ? '#17212b' : '#fff';
};
const darken = color => '#' + rgb(color).map(channel => Math.round(channel * 0.85).toString(16).padStart(2, '0')).join('');
const lighten = (color, amount) => '#' + rgb(color).map(channel =>
    Math.round(channel + (255 - channel) * amount).toString(16).padStart(2, '0')).join('');
const assetUrl = value => {
    try {
        const url = new URL(value, window.location.origin);
        return typeof value === 'string' && value && ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};
const rootVariables = new Set();
const ownedVariables = [
    '--app-color', '--app-primary-strong', '--app-primary-gradient', '--app-action-color', '--app-action-strong', '--app-page-background',
    '--app-menu-hover-background', '--app-surface-raised', '--app-surface-border',
    '--app-surface', '--app-soft-surface', '--app-text', '--app-muted-text', '--app-border-color',
    '--app-calendar-grid-line', '--app-calendar-event-overlay',
    '--mainText', '--bs-heading-color', '--bs-body-color', '--bs-card-bg', '--bs-card-color', '--bs-border-color',
    '--bs-link-color', '--bs-link-color-rgb', '--bs-link-hover-color',
    '--portal-primary', '--portal-primary-strong', '--portal-primary-rgb', '--portal-action-blue', '--portal-action-blue-dark',
    '--portal-success', '--portal-danger', '--portal-info', '--portal-warning-border', '--portal-surface-soft',
    '--portal-text', '--portal-surface', '--portal-sidebar-bg', '--portal-page-bg', '--portal-card-bg', '--portal-border',
    ...colorNames.flatMap(name => [
        '--app-' + name + '-color', '--app-' + name + '-rgb', '--app-' + name + '-ink', '--app-' + name + '-tint',
        '--app-on-' + name, '--bs-' + name, '--bs-' + name + '-rgb', '--' + name,
    ]),
];

function style(id, css) {
    let element = document.getElementById(id);
    if (!element) {
        element = document.createElement('style');
        element.id = id;
        document.head.appendChild(element);
    }
    element.textContent = css;
}

export function applyApplicationBranding(branding = {}) {
    window.__env = window.__env || {};
    window.__env.branding = branding;
    const root = document.documentElement;
    // Also clear variables set by an earlier HMR module instance on localhost.
    new Set([...ownedVariables, ...rootVariables]).forEach(key => root.style.removeProperty(key));
    rootVariables.clear();
    const variable = (key, value) => {
        root.style.setProperty(key, value);
        rootVariables.add(key);
    };
    let css = '';
    const colors = branding.colors || {};
    const primary = hex(colors.primary_color);
    const secondary = hex(colors.secondary_color);
    const surface = hex(colors.white_color) || '#fff';
    const text = contrast(surface, hex(colors.dark_color) || '#1f2937');
    const readableAccent = value => {
        let result = value;
        const darkSurface = luminance(surface) < 0.2;
        const target = darkSurface ? 5.5 : 4.5;
        for (let step = 0; step < 12 && colorContrastRatio(result, surface) < target; step++) {
            result = darkSurface ? lighten(result, 0.1) : darken(result);
        }
        return colorContrastRatio(result, surface) >= 4.5 ? result : contrast(surface);
    };
    if (hex(colors.color)) variable('--app-color', colors.color);
    for (const name of colorNames) {
        const value = name === 'primary' ? primary : hex(colors[name + '_color']);
        if (!value) continue;
        const channels = rgb(value).join(', ');
        const foreground = contrast(value, ['light', 'white'].includes(name) ? text : '#fff');
        const hoverForeground = contrast(darken(value), foreground);
        const ink = readableAccent(value);
        variable('--app-' + name + '-color', value);
        variable('--app-' + name + '-rgb', channels);
        variable('--app-' + name + '-ink', ink);
        variable('--app-' + name + '-tint', `color-mix(in srgb,${value} 8%,${surface})`);
        variable('--app-on-' + name, foreground);
        variable('--bs-' + name, value);
        variable('--bs-' + name + '-rgb', channels);
        variable('--' + name, value);
        if (['success', 'danger', 'info'].includes(name)) variable('--portal-' + name, value);
        if (name === 'warning') variable('--portal-warning-border', value);
        if (name === 'light') variable('--portal-surface-soft', value);
        if (name === 'dark') variable('--portal-text', value);
        if (name === 'white') {
            variable('--portal-surface', value);
            variable('--portal-sidebar-bg', value);
        }
        // The Sass theme contains compiled colors; Bootstrap variables alone
        // do not change those rules. Override semantic components explicitly.
        css += `
            .text-${name}{color:${['white', 'light'].includes(name) ? value : ink}!important}
            .bg-${name},.badge-${name}{background-color:${value}!important}
            .border-${name}{border-color:${value}!important}
            .btn-${name}:not(.portal-user-toggle){background:${value}!important;border-color:${value}!important;color:${foreground}!important;--bs-btn-bg:${value};--bs-btn-color:${foreground}}
            .btn-${name}:not(.portal-user-toggle):hover,.btn-${name}:not(.portal-user-toggle):focus,.btn-${name}:not(.portal-user-toggle):active{background:${darken(value)}!important;border-color:${darken(value)}!important;color:${hoverForeground}!important}
            .btn-outline-${name}{color:${ink}!important;border-color:${value}!important;--bs-btn-color:${ink}}
            .btn-outline-${name}:hover,.btn-outline-${name}:focus,.btn-outline-${name}:active{color:${hoverForeground}!important;background-color:${value}!important}
            .alert-${name}{color:${darken(value)};border-color:rgba(${channels},.3);background-color:rgba(${channels},.12)}
            .badge-soft-${name},.bg-soft-${name},.portal-status-${name}{color:${ink}!important;background:rgba(${channels},.12)!important;border-color:rgba(${channels},.25)!important}
            .portal-button-${name},.portal-icon-${name}{background:linear-gradient(135deg,${value},${darken(value)})!important;color:${foreground}!important}
            .portal-button-${name}:hover{background:${darken(value)}!important;color:${hoverForeground}!important}
        `;
    }
    if (primary) {
        variable('--portal-primary', primary);
        variable('--portal-primary-strong', darken(primary));
        variable('--app-primary-strong', darken(primary));
        variable('--portal-primary-rgb', rgb(primary).join(', '));
        variable('--app-primary-gradient', `linear-gradient(135deg,${primary},${darken(primary)})`);
        variable('--portal-card-bg', `color-mix(in srgb,${primary} 5%,${surface})`);
        variable('--portal-border', `color-mix(in srgb,${primary} 14%,${surface})`);
        variable('--bs-link-color', readableAccent(primary));
        variable('--bs-link-color-rgb', rgb(readableAccent(primary)).join(', '));
        variable('--bs-link-hover-color', darken(readableAccent(primary)));
        variable('--app-primary-ink', readableAccent(primary));
        const onPrimary = contrast(primary, '#fff');
        css += `
            a:not(.btn):not(.dropdown-item):not(.portal-sidebar-link){--bs-link-color:${readableAccent(primary)}}
            .form-check-input:checked,.page-item.active .page-link,.nav-pills .nav-link.active{background-color:${primary}!important;border-color:${primary}!important;color:${onPrimary}!important}
            .form-switch .form-check-input:checked{background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Ccircle cx='8' cy='8' r='3' fill='${onPrimary.replace('#', '%23')}'/%3E%3C/svg%3E")!important}
            .form-control:focus,.form-select:focus{border-color:${primary};box-shadow:0 0 0 .2rem rgba(${rgb(primary).join(', ')},.2)}
            .nav-tabs .nav-link.active{color:${primary};border-bottom-color:${primary}}
            #side-menu>li.mm-active>a:not(.router-link-exact-active){background:rgba(${rgb(primary).join(', ')},.1)!important;color:${readableAccent(primary)}!important}
            #side-menu a.router-link-exact-active,#side-menu li:has(>a.router-link-exact-active){background:${primary}!important;color:${onPrimary}!important;border-radius:10px}
            #side-menu a.router-link-exact-active::after{background:${primary}!important}
            #side-menu a.router-link-exact-active .nav-icon,#side-menu a.router-link-exact-active .submenu-chevron{color:${onPrimary}!important}
            .portal-user-avatar{background:var(--app-primary-gradient)!important;color:${onPrimary}!important}
            .portal-button-primary,.portal-booking-search-button,.register-submit,.patient-login-submit{background:var(--app-primary-gradient)!important;color:${onPrimary}!important}
        `;
    }
    // Independent fields: an omitted secondary color keeps its compiled defaults,
    // even when primary is customized (and vice versa).
    if (secondary) {
        variable('--app-action-color', secondary);
        variable('--app-action-strong', darken(secondary));
        variable('--portal-action-blue', secondary);
        variable('--portal-action-blue-dark', darken(secondary));
    }
    if (hex(colors.light_color)) {
        variable('--portal-page-bg', colors.light_color);
        variable('--app-page-background', primary
            ? `radial-gradient(circle at top right,rgba(${rgb(primary).join(',')},.06),transparent 50%),${colors.light_color}`
            : colors.light_color);
        css += `body,.main-content{background-color:${colors.light_color}}`;
        css += `.appointment-booking-page,.patient-login-page,.register-page{background:var(--app-page-background)!important}`;
    }
    if (hex(colors.dark_color) || hex(colors.white_color)) {
        variable('--portal-text', text);
        css += `body,.table,.form-control,.form-select,.dropdown-menu,.modal-content,h1,h2,h3,h4,h5,h6{color:${text}}`;
        css += `.page-title-box h4,.card-title,.card-header h4{color:${text}!important}`;
    }
    if (hex(colors.white_color)) {
        css += `.card,.modal-content,.dropdown-menu,.vertical-menu,.navbar-brand-box,#page-topbar,.portal-topbar,
                .patient-login-topbar,.appointment-booking-topbar,.register-card,.patient-login-card{background-color:${surface}}
                .portal-user-toggle{background:transparent!important}`;
    }
    // Dark-specific surfaces are opt-in. No settings, or a light surface,
    // leaves the existing compiled light theme and its layout untouched.
    if (hex(colors.white_color) && luminance(surface) < 0.2) {
        const raised = lighten(surface, 0.035);
        const hover = lighten(surface, 0.075);
        const border = lighten(surface, 0.13);
        const muted = contrast(surface, '#a8b6c9');
        variable('--app-surface-raised', raised);
        variable('--app-surface-border', border);
        variable('--app-menu-hover-background', hover);
        variable('--app-surface', surface);
        variable('--app-soft-surface', raised);
        variable('--app-text', text);
        variable('--app-muted-text', muted);
        variable('--app-border-color', border);
        variable('--app-calendar-grid-line', lighten(surface, 0.07));
        variable('--app-calendar-event-overlay', 'none');
        variable('--mainText', text);
        variable('--bs-heading-color', text);
        variable('--bs-body-color', text);
        variable('--bs-card-bg', surface);
        variable('--bs-card-color', text);
        variable('--bs-border-color', border);
        css += `
            :root{color-scheme:dark}
            .form-control:not(.form-control-color),.form-select,.input-group-text{background-color:${raised}!important;color:${text}!important;border-color:${border}!important}
            .form-control::placeholder{color:${muted};opacity:1}
            select option{background:${raised};color:${text}}
            .form-label,.form-check-label{color:${text}}
            .form-text,.text-muted{color:${muted}!important}
            .form-check-input:not(:checked){background-color:${raised};border-color:${border}}
            .form-switch .form-check-input:not(:checked){background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Ccircle cx='8' cy='8' r='3' fill='${muted.replace('#', '%23')}'/%3E%3C/svg%3E")!important}
            .dropdown-menu,.swal2-popup,.flatpickr-calendar{background:${raised}!important;color:${text}!important;border-color:${border}!important}
            .dropdown-item,.swal2-title,.swal2-html-container,.flatpickr-day:not(.selected):not(.startRange):not(.endRange){color:${text}!important}
            .dropdown-item:hover,.dropdown-item:focus,.flatpickr-day:hover{background:${hover}!important}
            .flatpickr-months .flatpickr-month,.flatpickr-current-month,.flatpickr-weekdays,span.flatpickr-weekday{background:${raised};color:${text}}
            .flatpickr-day.flatpickr-disabled,.flatpickr-day.prevMonthDay,.flatpickr-day.nextMonthDay{color:${muted}!important}
            .card,.card-body,.modal-content{background-color:${surface}!important;color:${text};border-color:${border}!important}
            .card-header,.card-footer{background-color:${raised}!important;color:${text}!important;border-color:${border}!important}
            .appointment-booking-topbar,.patient-login-topbar{background:${surface}!important;border-color:${border}!important}
            .appointment-booking-security{color:${muted}!important}
            .breadcrumb-item,.breadcrumb-item.active,.breadcrumb-item::before{color:${muted}!important}
            .dcc-grid-card .dcc-scroller .booking:hover{background:color-mix(in srgb,var(--booking-color,#60a5fa) 24%,${raised})!important}
            .dcc-grid-card .booking.has-break-after::after{background:repeating-linear-gradient(-45deg,${border} 0 4px,${raised} 4px 8px)}
            .fc{--fc-border-color:${border};--fc-page-bg-color:${surface};--fc-neutral-bg-color:${raised};color:${text}}
            .fc .fc-col-header-cell,.fc .fc-list-day-cushion{background:${raised}!important}
            .fc .fc-event .be-name,.fc .fc-event .be-doctor,.fc .fc-event .be-time{color:${text}!important}
            .portal-booking-input-wrap,.portal-booking-dropdown{background:${raised}!important;border-color:${border}!important;color:${text}!important}
            .portal-booking-input-wrap input::placeholder{color:${muted}}
            .portal-booking-dropdown-item{color:${text}}
            .portal-booking-dropdown-item:hover{background:${hover}!important}
            .portal-booking-step::before{background:${border}}
            .portal-booking-step:not(.active):not(.done)>span{background:${raised};color:${muted};border:1px solid ${border}}
            .portal-booking-step.active>span,.portal-booking-step.done>span{color:${primary ? contrast(primary, '#fff') : '#fff'}}
            .nav-link:not(.active),.tab-card-nav .nav-link:not(.active){color:${muted}}
            .nav-link.disabled,.nav-link[aria-disabled="true"]{color:${muted}!important}
            .nav-pills .nav-link.active,.tab-card-nav .nav-link.active{background:${hover}!important;color:${text}!important}
            .nav-tabs{border-color:${border}!important}
            .nav-tabs .nav-link.active,.nav-tabs .nav-item.show .nav-link{background:${hover}!important;color:${primary ? readableAccent(primary) : text}!important;border-color:${border}!important}
            .nav-tabs .nav-link:not(.active):hover{background:${raised};border-color:${border}}
            .portal-assessment-list-item,.portal-assessment-list-item-featured,
            .portal-assessment-page-card,.portal-assessment-question,.portal-assessment-progress,
            .portal-appointment-card,.portal-appointment-modal,.portal-delete-modal,
            .portal-profile-section,.portal-profile-notice,.portal-message-detail-card,
            .portal-message-dashboard-row,.portal-booking-doctor-card,.portal-booking-summary-card,
            .portal-assessment-empty,.documents-empty,.document-card,.documents-modal,
            .documents-no-bookings{background:${raised}!important;border-color:${border}!important;color:${text}!important}
            .portal-assessment-list-item h3,.portal-assessment-list-item p,
            .portal-assessment-question label,.portal-appointment-card strong{color:${text}}
            .portal-assessment-appointment,.portal-assessment-list-item small{color:${muted}}
            .documents-empty h2,.document-card h3,.documents-modal h2,.documents-modal label{color:${text}!important}
            .documents-empty p,.document-card p,.documents-modal p,.documents-password-field{color:${muted}!important}
            .documents-empty>span,.document-icon,.document-shares span{
                background:${hover}!important;color:${primary ? readableAccent(primary) : text}!important}
            .documents-modal-close,.documents-password-input button{
                background:transparent;color:${muted}!important}
            .documents-modal-close:hover,.documents-password-input button:hover{background:${hover}!important}
            .documents-password-field input{background:${surface};color:${text};border-color:${border}!important}
            .document-outline{background:transparent!important;color:${primary ? readableAccent(primary) : text}!important;border-color:${border}!important}
            .documents-empty.is-error,.documents-password-error{
                background:color-mix(in srgb,${hex(colors.danger_color) || '#fda4af'} 8%,${surface})!important;
                color:${hex(colors.danger_color) || '#fda4af'}!important;border-color:${border}!important}
            .vertical-menu:not(.sidebar-collapsed) #sidebar-menu ul.sub-menu{background:transparent!important;border-color:${border}!important}
            #sidebar-menu .side-nav-link-ref:not(.router-link-exact-active),
            #sidebar-menu .side-nav-link-a-ref,#sidebar-menu .is-parent{color:${text}!important}
            #sidebar-menu .menu-point-title,#sidebar-menu .nav-icon,#sidebar-menu .submenu-chevron{color:inherit!important}
            .vertical-menu #sidebar-menu .sub-menu .side-nav-link-ref:not(.router-link-exact-active),
            .vertical-menu #sidebar-menu .sub-menu .side-nav-link-a-ref{color:${text}!important}
            .vertical-menu #sidebar-menu .sub-menu .menu-point-title,
            .vertical-menu #sidebar-menu .sub-menu .nav-icon,
            .vertical-menu #sidebar-menu .sub-menu .submenu-chevron{color:inherit!important}
            #sidebar-menu a:not(.router-link-exact-active):hover,
            #sidebar-menu a:not(.router-link-exact-active):focus-visible,
            .portal-sidebar-logout-button:hover,.portal-sidebar-logout-button:focus-visible,
            .mobile-side-nav-item:hover,.mobile-side-nav-item:focus-visible{
                background:var(--app-menu-hover-background)!important;color:${primary ? readableAccent(primary) : text}!important}
            .vertical-menu #sidebar-menu .sub-menu .side-nav-link-ref:not(.router-link-exact-active):hover,
            .vertical-menu #sidebar-menu .sub-menu .side-nav-link-ref:not(.router-link-exact-active):focus-visible,
            .vertical-menu #sidebar-menu .sub-menu .side-nav-link-a-ref:hover,
            .vertical-menu #sidebar-menu .sub-menu .side-nav-link-a-ref:focus-visible{
                background:var(--app-menu-hover-background)!important;color:${primary ? readableAccent(primary) : text}!important}
            .vertical-menu #sidebar-menu .sub-menu>li:hover:not(:has(>a.router-link-exact-active)){background:transparent!important}
            #sidebar-menu .sub-menu a:not(.router-link-exact-active)::after{background:transparent!important}
            #sidebar-menu a.router-link-exact-active .menu-point-title,
            #sidebar-menu a.router-link-exact-active .nav-icon{color:inherit!important}
            ${primary ? `.vertical-menu #sidebar-menu .sub-menu a.side-nav-link-ref.router-link-exact-active{
                background:${primary}!important;color:${contrast(primary, '#fff')}!important}` : ''}
            .vertical-menu.sidebar-collapsed #sidebar-menu.menu-sm .metismenu ul.sub-menu{
                background:${raised}!important;border-color:${border}!important}
            .vertical-menu.sidebar-collapsed #sidebar-menu.menu-sm .metismenu ul.sub-menu>li:hover:not(.mm-active):not(:has(>a.router-link-active)):not(:has(>a.router-link-exact-active)){
                background:var(--app-menu-hover-background)!important}
            ${primary ? `
                .vertical-menu.sidebar-collapsed #sidebar-menu.menu-sm .metismenu ul.sub-menu>li:has(>a.router-link-exact-active){
                    background:${primary}!important;color:${contrast(primary, '#fff')}!important}
                .vertical-menu.sidebar-collapsed #sidebar-menu.menu-sm .metismenu ul.sub-menu>li>a.side-nav-link-ref.router-link-exact-active{
                    background:transparent!important;color:${contrast(primary, '#fff')}!important}
                .vertical-menu.sidebar-collapsed #sidebar-menu.menu-sm .metismenu ul.sub-menu>li>a.side-nav-link-ref.router-link-exact-active .nav-icon,
                .vertical-menu.sidebar-collapsed #sidebar-menu.menu-sm .metismenu ul.sub-menu>li>a.side-nav-link-ref.router-link-exact-active .menu-point-title{
                    color:inherit!important}` : ''}
            .portal-sidebar-logout{padding-left:16px;padding-right:16px}
            .portal-sidebar-logout-button{min-height:40px}
            .dropdown-item{color:${text}}
            .dropdown-item:hover,.dropdown-item:focus{background:${hover};color:${text}}
            .mobile-side-nav-item,.mobile-side-nav-back,.mobile-side-nav-icon{color:${text}}
        `;
    }
    style('application-branding-theme', css);
    let link = document.getElementById('application-branding-css-file');
    const cssUrl = assetUrl(branding.css_url);
    if (cssUrl) {
        if (!link) {
            link = document.createElement('link');
            link.id = 'application-branding-css-file';
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        link.href = cssUrl;
    } else {
        link?.remove();
    }
    style('application-branding-css', typeof branding.css === 'string' ? branding.css : '');
    window.dispatchEvent(new Event('application-branding-updated'));
}

export async function refreshApplicationBranding(axios) {
    try {
        const response = await axios.get('public/application-branding', {timeout: 5000});
        applyApplicationBranding(response.data?.data || {});
    } catch {
        // A missing/unavailable appearance endpoint must never prevent login.
        applyApplicationBranding(window.__env?.branding || {});
    }
}

export async function initializeApplicationBranding(axios) {
    if (window.__env?.branding?.application_id) {
        applyApplicationBranding(window.__env.branding);
    } else {
        await refreshApplicationBranding(axios);
    }
}

// Keep each image's own compiled fallback: admin dark/light and portal logos differ.
export const applicationLogo = {
    mounted(element) {
        const fallback = element.getAttribute('src');
        const update = () => {
            element.setAttribute('src', assetUrl(window.__env?.branding?.logo_url) || fallback);
        };
        const onError = () => {
            if (element.getAttribute('src') !== fallback) element.setAttribute('src', fallback);
        };
        element.__applicationLogoCleanup = () => {
            window.removeEventListener('application-branding-updated', update);
            element.removeEventListener('error', onError);
        };
        window.addEventListener('application-branding-updated', update);
        element.addEventListener('error', onError);
        update();
    },
    unmounted(element) {
        element.__applicationLogoCleanup?.();
    },
};
